/**
 * **对局屏（热座）。**
 *
 * 规则层 → `project()` → 画面，这一条链在这里第一次完整接起来。
 * 它同时也是**联机层的预演**：屏幕只吃 `PlayerView`（`project` 的输出），
 * 一行都不碰 `GameState`；`act()` 是唯一的写入口。将来把热座换成
 * `S3Session` 时，这一屏改的只有"数据从哪来、action 往哪去"。
 *
 * ## 其他玩家的手牌：平时是一个数字，被抽牌时才是一扇牌背
 *
 * 评审两条：
 *
 * - 「在正常打牌时……其他人的手牌只显示为一个"他有几张牌"的数字」
 * - 「在抽牌期间，需要展示一手由牌背构成的其他人的手牌牌扇」
 *
 * 所以座位条上默认印的是**张数**（`handCount`，它本来就是公开信息）；
 * 只有当某人的手牌**正被指着**（有人打了两张同名、目标是他的话）时，
 * 才换成 `HandBacks` 铺出来的一扇牌背。判定只有一处：`exposedHands()`。
 *
 * ⚠️ 牌背**没有身份**，只有位次。所以被抽牌的人理牌时，这一屏跟着动，
 * 而抽牌的人一个 bit 的手牌信息都没多拿到 —— 见 `HandBacks` 的文件头。
 *
 * ## 手牌顺序
 *
 * 评审：「使玩家*随时*（回合内外）都可以调换自己的卡牌顺序」。
 * 手牌模块本身支持拖拽换位（横向拖 = 换位，拖过线 = 打出），
 * 这里做的是把"视觉顺序"**回写成状态**（`hand/reorder`）——
 * 于是顺序能被同步出去，被抽牌的人理牌、对方屏幕上的牌背跟着动这件事
 * 在联机下才成立。
 */

import { Container } from 'pixi.js';
import type { Ticker } from 'pixi.js';
import { CardFlip } from '../../render/game/cardFlip';
import { CenteredStage } from '../../render/game/centeredCard';
import { PlayReveal, playSignature } from '../../render/game/playReveal';
import {
  DrawReveal,
  REVEAL_CARD_HEIGHT,
  type ArrivalPose,
} from '../../render/game/drawReveal';
import { Hand } from '../../render/hand';
import { CardTooltip, syncCardTooltip } from '../../render/game/cardTooltip';
import { richText } from '../../i18n';
import type { GameAction } from '../../core/actions';
import type { CardDef } from '../../core/card';
import { cardName, cardNameResolver } from '../../core/cardText';
import { playerId, type CardTitle, type CardUid, type PlayerId } from '../../core/ids';
import type { PlayerView } from '../../core/project';
import { createHotSeat, type HotSeat } from '../../hotseat';
import { publishGame } from '../../dev/registry';
import { reportEnergy } from '../../audio/report';
import type { Screen, ScreenContext } from '../screen';
import { displayNickname } from '../settings';
import { createOnlineGameScreen } from './gameOnline';
import { createTableChrome, type TableChrome } from './gameTable';
import { ImpactParticles } from '../../render/game/impactParticles';
import { armActionOf, handCardAction, handIsLive, intentOf, parsePlay, playCards } from './tableIntent';
import { handOrderMoves } from './handOrder';
import { OVERLAY } from './ids';

/**
 * 手牌占画面底部的多高。
 *
 * ⚠️ 版面其余部分**不再由这个文件排** —— 回合队列、抽牌堆、弃牌堆、
 * 结束回合、齿轮都在 `gameTable.ts`（热座和联机共用）。这里只剩手牌的尺寸。
 */
const HAND_HEIGHT = 132;

/**
 * **联机对局**的工厂（`/game`）。
 *
 * ⚠️ 只有**这一趟会话里真的有联机会话**（`ctx.online()` 非空）时才建对局屏；
 *    没有（典型就是刷新之后）就走兜底回退，**绝不**偷偷开一局热座顶上来。
 *    热座走自己的 `SCREEN.hotseat`（见 `createHotseatScreen`）。
 */
export function createOnlineScreen(ctx: ScreenContext): Screen {
  const room = ctx.online?.() ?? null;
  const online = room === null ? null : createOnlineGameScreen(ctx, room);
  if (online !== null) return online;
  /**
   * 兜底屏：什么都不画，进场就退回上一层。
   *
   * 正常路径上到不了这里 —— `main.ts` 的 `navigate` 已经先问过 `online`。
   * 留着它是为了防止「地址要求联机、手里却没有会话」时白屏或者开错局。
   */
  const empty = new Container();
  empty.label = 'screen-game-fallback';
  return {
    container: empty,
    onEnter() {
      // 延到微任务：在 `sync()` 里直接再改栈会递归
      queueMicrotask(() => ctx.stack.pop());
    },
    destroy() {
      empty.destroy({ children: true });
    },
  };
}

/**
 * **热座对局**（`/hotseat`）。
 *
 * 和联机共用整套桌面装置与手牌，只是没有 `S3Session`：状态全在本机内存里，
 * 所以刷新就是重开一局 —— 这正是它单独占一个地址的原因。
 */
export function createHotseatScreen(ctx: ScreenContext): Screen {
  const renderer = ctx.renderer;
  const game = createTable(ctx);
  const container = new Container();
  container.label = 'screen-game';
  container.eventMode = 'static';

  /**
   * 翻牌动画层。⚠️ **不在 `dynamic` 里面** —— 那个容器每次状态变化都会被清空，
   * 而翻牌要跨好几帧、跨好几次状态更新（抽牌事件之后紧接着就是视图更新）
   */
  const effects = new Container();
  /**
   * 手牌：不随状态重建，只换牌。
   *
   * ⚠️ **先加手牌、再加桌面装置、最后是特效**：评审要求"UI 应该盖在手牌层
   * 上方"，而翻牌动画要盖在所有东西上面（它是"抽出来的那张牌"）。
   */
  const hand = new Hand({
    input: ctx.input,
    renderer,
    faces: ctx.faces,
    bounds: { width: ctx.bounds.width, height: ctx.bounds.height },
    height: HAND_HEIGHT,
    cards: [],
    prewarm: false,
    // 拖过出牌线 = 想打这组牌。能找到唯一匹配的选项就替他点一下
    onPlay: (played) => playByTitles(played),
  });
  hand.container.position.set(0, 0);
  container.addChild(hand.container);

  /** 桌面装置挂在这一层里（它会在换 UI 倍率时整块重建） */
  const chromeLayer = new Container();
  chromeLayer.label = 'table-chrome-layer';
  /**
   * 齿轮菜单那一层。
   *
   * ⚠️ **加在所有东西之后**：评审要"按下设置按钮后弹出的菜单盖在整个屏幕之上"，
   * 而翻牌特效是最后一层 —— 菜单得比它还上面。
   */
  const menuLayer = new Container();
  menuLayer.label = 'table-menu-layer';
  /**
   * **悬停说明那一层。**
   *
   * ⚠️ 加在**最后**（比手牌、装置、翻牌特效、齿轮菜单都靠后）：说明是一块
   *    "浮在最上面的纸"，被谁压住都读不了。手牌内部那套 zIndex 排序管不到
   *    它 —— 它是另一层，靠层的先后压住牌。
   */
  const tooltipLayer = new Container();
  tooltipLayer.label = 'table-tooltip-layer';
  /**
   * ★ **打击粒子**（评审：IMPACT 那一把碎三角）。
   *
   * ⚠️ 加在 `effects` **之前** —— 评审要的是"z-index 在漂浮于屏幕中心的异常牌
   *    之下"（`effects` 里是手牌 / 翻牌 / 正中那张牌），而在背景 / 濒死层之上。
   */
  const particles = new ImpactParticles();
  // ⚠️ `menuLayer`（overlay：记牌器 / 抽牌堆 / 齿轮菜单）在说明层**下面**：
  //    说明要盖在 overlay 上，否则 hover 牌时说明会被窗口挡住
  container.addChild(chromeLayer, particles.view, effects, menuLayer, tooltipLayer);
  /** 正在翻的牌（被抢走的那一张）。走完就自己删掉 */
  const flips: CardFlip[] = [];

  /**
   * **正在飞的那几张牌**（`DrawReveal`）—— 结束回合抽到的那张。
   *
   * ⚠️ 它是"抽牌"这一件事的画面，不是"牌在哪"的真相。真相在状态里，
   * 这里只负责在它落进手牌之前，先把这段路走完。
   */
  const reveals: Array<{
    view: DrawReveal;
    /**
     * **已经交班给"浮在正中那张牌"了吗**（评审：浮在屏幕中央的牌要有手牌的
     * 一切属性 —— 自然摆动 / 随音乐鼓动 / hover 看 lore）。
     *
     * 翻牌那一段（`DrawReveal`）走完、牌在正中最稳的那一刻换手；换手之后
     * `view` 被销毁，这一条只剩"等 `centeredStage` 说它走了"。
     */
    handedOff: boolean;
    readonly uid: CardUid;
    /** 这一张是"飞进手牌"那种（另一种是"停在正中"的致命牌） */
    readonly arrival: boolean;
    /** 这一张是不是造成濒死的那张 */
    readonly dying: boolean;
    /** 濒死已经放行了吗（飞到中点那一刻放） */
    dyingReleased: boolean;
    /** 落地那一记小 kick 放了没有（"牌砸在桌上"，和主打击分开） */
    landed: boolean;
  }> = [];
  /**
   * **事件里来了、还没摆出来的抽牌。**
   *
   * ⚠️ 为什么不在事件回调里直接建：那一刻 `view()` 虽然已经更新了，但
   * **手牌模块还没喂**（`syncHand` 在 `build()` 的微任务里）—— 而落点要
   * 现读手牌那一格。所以事件只把"抽到了什么"记下来，摆的动作交给 `build()`。
   */
  let pendingReveals: Array<{
    readonly uid: CardUid;
    readonly def: CardDef;
    readonly hold: boolean;
    /** 这一手是谁抽的。⚠️ 事件里有它，而 `view().you` 那时**已经换人了** */
    readonly actor: PlayerId;
  }> = [];
  /**
   * **正在飞的出牌**（`PlayReveal`）—— 视图里出现待结算出牌时摆一次。
   *
   * 触发点是 `view.resolution`：每次出现一个**新的**待结算出牌就摆一拨，
   * 打断链增长（同一个 `play`）不重摆。见 `syncPlayReveals`。
   */
  const plays: PlayReveal[] = [];
  /** 上一次摆过的出牌签名。`null` = 上一帧没有待结算出牌 */
  let lastPlaySignature: string | null = null;
  /**
   * **这一手打出的牌，起飞点在哪**（牌心，画布坐标）。
   *
   * 玩家是**从手里把牌拖出去**的，所以出牌动画该从拖拽位置起飞，
   * 而不是固定从某个下方锚点冒出来（用户报的正是这个）。
   * `commit()` 在动作生效**之前**把它量下来；`null` = 量不到，回退到
   * 桌面装置的 `playOrigin()`。
   */
  let pendingPlayFrom: { readonly x: number; readonly y: number }[] | null = null;
  /**
   * **正在飞进手牌的那几张**（uid）。
   *
   * 手牌为它们预留了一格（`Hand.reserveArrival`），所以在它们落地之前
   * **不能喂给手牌** —— 见 `syncHand()`。
   */
  let arriving: CardUid[] = [];
  /**
   * **停在正中的那几张**（致命牌）：牌就在手里，但这一会儿不显示。
   * 见 `applyHidden()`。
   */
  let inFlight: CardUid[] = [];
  /**
   * **抽牌动画期间把视角钉在谁身上。**
   *
   * 评审：「牌飞入手牌只是垂直向下移动，并没有移动到其准确位置。」根因之一
   * 是热座**抽完的同一瞬间就把设备交给了下一个人** —— 那张牌于是要飞进
   * 下一个人的手里。钉住之后手牌还是抽牌人的，落点才对得上（见
   * `HotSeat.holdActor`）。动画一结束就放掉。
   */
  let holdActorId: PlayerId | null = null;
  /** 走了几帧。**诊断用**（"卡住不动"要分清是没帧还是逻辑没走） */
  let frameCount = 0;
  /**
   * **新牌落进手牌之后，视角再钉住多久**（毫秒）。
   *
   * 评审：「新牌到位后告诉手牌代码，令其渲染为添加完牌后的状态并删除新牌。」
   * 手牌确实切成了 "n+1 张" 那个状态 —— 但它得**画出来给人看一眼**再交设备
   * （钉一放开，手牌立刻换成下一个人的，那张刚落的牌就一闪而过）。
   *
   * 所以：落地 → 手牌渲染 n+1 → 停这么一下 → 放掉视角、设备交给下一个人。
   */
  const HANDOFF_MS = 320;
  /** 离"交设备"还有多久。`0` = 没有在等 */
  let handoffLeft = 0;

  /** 每个人一扇牌背（只有被抽牌的人才铺出来） */
  let unsubscribe: (() => void) | null = null;
  let offEvents: (() => void) | null = null;
  let dirty = false;

  /** 上一次喂给手牌的 uid 顺序。用来发现"视觉顺序被拖动了" */
  let lastHandOrder: CardUid[] = [];

  /**
   * **桌面装置**：回合队列 / 抽牌堆 / 弃牌堆 / 结束回合 / 齿轮菜单 / 状态与动作。
   *
   * 它跨帧活着（队列在滚动），所以是**建一次、每帧驱动**，不是每次视图变化
   * 重建 —— `update()` 只换内容。
   *
   * ⚠️ 换 UI 倍率时**必须重建**：那些件在构造时就把 `uiPad(...)` 烘进了自己的
   * 尺寸里（齿轮的边长、牌背的高度、队列的行高），改倍率不重建的话它们会
   * 和旁边的按钮对不上。`rebuild()` 那条路会走到这里。
   */
  let chrome: TableChrome | null = null;
  let chromeScale = -1;

  function ensureChrome(): TableChrome {
    const s = ctx.settings.get().uiScale;
    if (chrome !== null && chromeScale === s) return chrome;
    chrome?.destroy();
    chrome = createTableChrome({
      ctx,
      renderer,
      // ⚠️ 包一层：`defOf` 是后面 `const` 定义的，直接引用会在**求值顺序**上
      //    踩到暂时性死区（回调要等视图更新时才跑，那时它早就有了）
      defOf: (title) => defOf(title),
      // 右下角那个按钮走的是同一条 `pass` 动作（见 `gameTable.ts`）
      onEndTurn: () => undefined,
      onSettings: () => ctx.stack.openOverlay(OVERLAY.settings),
      onLeave: () => ctx.stack.pop(),
      onAction: (action) => game.act(action),
      onPickBack: (slot) => pickBack(slot),
      // ★ 点了队列里的名字 = 给这一手指定对象
      onPickTarget: (id) => pickTarget(id),
    });
    chromeScale = s;
    chromeLayer.addChild(chrome.container);
    // ★ 取牌扇要盖在出牌/翻牌特效之上（在 container 里它会被 effects 挡住）
    container.addChildAt(chrome.fanLayer, container.getChildIndex(effects) + 1);
    menuLayer.addChild(chrome.overlay);
    return chrome;
  }

  // ============================================================
  // 悬停说明
  // ============================================================

  let tooltipScale = ctx.settings.get().uiScale;
  let tooltip = createTooltip();

  function createTooltip(): CardTooltip {
    const t = new CardTooltip({
      scale: tooltipScale,
      bounds: ctx.bounds,
      cardNameOf: cardNameResolver(ctx.registry),
      comboRules: () => view().config.combos,
      loreSections: () => ctx.settings.get().loreSections,
    });
    tooltipLayer.addChild(t.container);
    return t;
  }

  /**
   * UI 倍率变了就重建说明面板。
   *
   * ⚠️ 和桌面装置一样：字号/内边距是**建的时候**按倍率烘进去的，不重建的话
   *    说明会保持旧倍率的大小，和旁边的按钮对不上。
   */
  function ensureTooltip(): void {
    const s = ctx.settings.get().uiScale;
    if (s === tooltipScale) return;
    tooltip.destroy();
    tooltipScale = s;
    tooltip = createTooltip();
  }

  // ============================================================
  // 数据
  // ============================================================

  const view = (): PlayerView => game.view;

  const defOf = (title: CardTitle): CardDef | undefined => ctx.registry.get(title);

  // ============================================================
  // 手牌：视觉顺序回写成状态
  // ============================================================

  /**
   * **现在该喂给手牌模块的那一手** ——
   *
   * 顺序 = `view().yourHand`（状态里的顺序），滤掉正在飞过来的那几张
   * （`arriving`，见 `syncHand` 的说明）和查不到定义的。
   *
   * ⚠️ `syncHand` 和 `flushHandOrder` **必须用同一份**：一个喂进去、
   * 一个回写，判据不一致就会来回打架。
   */
  function liveHand(): { defs: CardDef[]; uids: CardUid[] } {
    const flying = new Set(arriving);
    const defs: CardDef[] = [];
    const uids: CardUid[] = [];
    for (const card of view().yourHand) {
      if (flying.has(card.uid)) continue;
      const def = defOf(card.title);
      if (def === undefined) continue;
      defs.push(def);
      uids.push(card.uid);
    }
    return { defs, uids };
  }

  /**
   * **玩家拖了手牌就把它回写成状态。**
   *
   * ⚠️ 判据是**当前状态里的顺序**（`liveHand`），不是"喂进去时的那一份"
   * （`lastHandOrder`）：回写一次之后状态就变了，拿着旧的喂入表去算位移
   * 会**每帧再挪一次**（旧实现的两个 bug 之一）。用当前顺序算，
   * 回写完成的那一刻 `target` 就是恒等置换，自然幂等。
   *
   * ⚠️ **拖拽进行中不回写**：`setCards` 会重建整手牌、把正在拖的那张打断。
   * 松手那一帧 `held` 回 -1，而视觉顺序已经定好了。
   */
  function flushHandOrder(): void {
    if (hand === null) return;
    if (hand.held >= 0) return;
    const fed = lastHandOrder;
    const order = hand.order;
    // 手牌张数和喂进去的时候不一致 → 正在换人 / 换牌，先别动
    if (order.length !== fed.length) return;
    const live = liveHand().uids;
    if (live.length !== fed.length) return;
    // 喂进去的那一手必须就是现在视图里这一手（只是顺序可能不同）；
    // 不等说明正在换人，绝不能把顺序写到别人头上
    const liveSet = new Set(live);
    if (!fed.every((uid) => liveSet.has(uid))) return;

    // 视觉顺序 → 它该变成的状态位次（纯函数，见 ui/screens/handOrder.ts）
    const moves = handOrderMoves(fed, order, live);
    if (moves === null) return;
    for (const [from, to] of moves) {
      if (!game.reorder(from, to)) break;
    }
  }

  /**
   * **松手前举牌。** 拖到"松手会打出"的有效高度就发 `turn/arm`（开打断窗口、
   * 不移动牌）；拖回线下就 `turn/arm/cancel`。松手提交走 `playByTitles`。
   */
  function syncArm(): void {
    if (hand === null) return;
    const out = hand.dragOut();
    if (out.length > 0) {
      if (armed === null) {
        const action = armActionOf(view(), uidsOfPlayed(out), ctx.registry);
        if (action !== null && action.t === 'turn/arm') {
          armed = action;
          if (!game.act(action)) armed = null;
        }
      }
    } else if (armed !== null) {
      game.act({ t: 'turn/arm/cancel', actor: armed.actor });
      armed = null;
    }
  }

  /** 把当前操作者的手牌喂给手牌模块 */
  function syncHand(): void {
    if (hand === null) return;
    /**
     * ⚠️ **正在飞过来的那几张先别喂。**
     *
     * 手牌已经给它们留好了位置（`reserveArrival`）—— 提前喂进来的话，那张牌
     * 会立刻出现在扇形里，而画面上还有一张正飞过来，看着像抽了两张。
     * 落地那一刻 `commitArrival()` + 这里重新喂一次，它才出现在该在的地方。
     */
    // ⚠️ `lastHandOrder` 必须和**喂进去的牌**一一对应：查不到定义的牌会被
    //    跳过，若还把它们记进顺序表，下一帧的长度检查就对不上、
    //    `applyHidden` 的下标也会错位（参考审计 M4）。`liveHand` 就是这条。
    const { defs, uids } = liveHand();
    const same = uids.length === lastHandOrder.length && uids.every((uid, i) => uid === lastHandOrder[i]);
    if (same) return;
    lastHandOrder = uids;
    hand.setCards(defs);
    // 手牌换了内容 → 说明可能指着已经不在的那一张
    // ⚠️ 这一处不是每帧路径，`dt` 给 0（`hide()` 不看它；`show()` 的重置在
    //    `show()` 自己那一侧，浮现动画由每帧那一处推进）
    syncTooltip(0);
  }

  /** 悬停说明的当前状态（诊断与冒烟用） */
  let tooltipState: { readonly title: string; readonly side: string } | null = null;

  /**
   * **摆悬停说明。**
   *
   * 每帧调（`updateFrame`），但只有"指着哪一张"变了才重建文字 ——
   * `Text` 每帧重建会一直重量字形。牌定义和包围盒都由 `Hand` 自己给
   * （`hoveredDef` / `hoveredBox`），见 `render/game/cardTooltip.ts` 的
   * `syncCardTooltip`。
   */
  function syncTooltip(dtMs: number): void {
    tooltip.resize(ctx.bounds);
    /**
     * ⚠️ **先问"浮在正中那张牌"**（评审：hover 屏幕中央的濒死牌是查看它 lore
     *    的唯一方式）。它更稀有、也更重要；两者一般也不会同时被指着
     *    （它在画面正中，手牌在底部）。
     */
    const centered = centeredStage.hovered();
    // 记牌器表头的小牌也可以 hover（它更靠上，和手牌/居中牌不会同时指着）
    const source = chrome?.trackerHover() ?? centered ?? hand;
    // ★ 动作按钮的说明**复用牌说明的同一块面板**：没指着牌、但指着按钮时显示它
    // 数字格的精确值说明优先于按钮说明（两者不会同时指着）
    const cell = chrome?.trackerCellTip() ?? null;
    const at = chrome?.actionTip() ?? null;
    const tip =
      cell !== null
        ? { title: cell.title, text: richText(cell.tip, cell.tipParams), box: cell.box }
        : at === null
          ? null
          : { title: at.title, text: richText(at.tip, at.tipParams), box: at.box };
    syncCardTooltip(tooltip, source, dtMs, tip);
    const def = source.hoveredDef;
    tooltipState = def === null ? null : { title: cardName(def), side: tooltip.side };
  }

  // ============================================================
  // 抽牌动画
  // ============================================================

  /**
   * 查看某张牌的定义。
   *
   * ① 先查看得见的两份（自己的手牌、弃牌堆）—— 绝大多数情况都在这儿；
   * ② ★ 查不到就问 `HotSeat.titleOf()`（见它的说明）：热座里**抽完的一瞬间
   *    操作者就换人了**，那张牌跑到了上一个人手里，谁的视图里都没有它。
   *    而抽牌动画本来就要把这张牌亮出来，所以这是唯一那个故意的口子。
   */
  function defOfUid(uid: CardUid): CardDef | undefined {
    const source = view();
    const pooled = [...source.yourHand, ...source.discardPile].find((card) => card.uid === uid);
    if (pooled !== undefined) return defOf(pooled.title);
    const title = game.titleOf(uid);
    return title === null ? undefined : defOf(title);
  }

  /**
   * **把"还在飞"这件事同步到手牌上**：那几张牌占着位、但不显示。
   *
   * ⚠️ 每次都按 uid 重算一遍索引（不是记住上一次的下标）：理牌、被人抢走一张
   * 都会让下标变，而记住下标的下场是"藏错了牌"——那张牌看上去凭空消失。
   */
  function applyHidden(): void {
    const cards = view().yourHand;
    const indices: number[] = [];
    for (const uid of inFlight) {
      const index = cards.findIndex((card) => card.uid === uid);
      if (index >= 0) indices.push(index);
    }
    // ★ 塞回异常时，那张异常由正中的浮牌代表，不该再出现在牌扇里
    const pending = view().pending;
    if (pending?.kind === 'insertLethal' && pending.revealedTitle !== null) {
      for (let i = 0; i < cards.length; i++) {
        if (cards[i]?.title === pending.revealedTitle && !indices.includes(i)) indices.push(i);
      }
    }
    hand.setHidden(indices);
  }

  /**
   * **把"停在正中的那一张"从翻牌层交接过来**（评审：浮在屏幕中央的牌要有
   * 手牌的一切属性 —— 自然摆动、随音乐鼓动、hover 看 lore）。
   *
   * 交接点在"停稳"那一刻（`DrawReveal.holding`），两边姿态完全一样，所以看不出来。
   *
   * ⚠️ **牌高是 `REVEAL_CARD_HEIGHT × DYING_GROW_TO`**：这就是它在屏幕上的最终
   *    大小。牌面按它烘，所以字天然是清晰的 —— 不要传"1 倍牌高再靠 scale 放大"。
   *
   * ⚠️ **hover 那几项压低了**：这一张身后压着濒死层预烘的辉光/阴影，按手牌那样
   *    抬 8% 牌高会和自己的光晕脱开。摆动、脉动、平滑这些一个字都没改。
   */
  const centeredStage = new CenteredStage({
    input: ctx.input,
    faces: hand.faces,
    layer: effects,
    bounds: ctx.bounds,
    defOf: (uid) => defOfUid(uid),
    /**
     * ⚠️ **hover 那几项压低**：这一张身后压着濒死层预烘的辉光/阴影，按手牌那样
     *    抬 8% 牌高会和自己的光晕脱开。摆动、脉动、平滑这些一个字都没改。
     */
    feel: { hoverLift: 0.03, hoverScale: 0.05, hoverPitch: 4, hoverYaw: 5 },
  });

  /**
   * 把 `pendingReveals` 摆成真的动画。
   *
   * 评审那段动作：「在抽牌堆顶部位置生成一张牌背，播放两个动画，同时开始同时
   * 结束……完成动画后稍微停顿，若造成濒死则停留在屏幕中央，若不是则飞入手牌。」
   */
  function spawnPendingReveals(): void {
    if (pendingReveals.length === 0) return;
    const pending = pendingReveals;
    pendingReveals = [];

    const chrome_ = ensureChrome();
    const from = chrome_.drawTop();
    /**
     * ★ **中点是画布正中**，不是"桌面正中"（`chrome.center()` 那个 `FAN_CENTER`）。
     *
     * 评审：「导致牌面几何中心偏离牌面。」濒死那层的辉光/阴影是围着**画布正中**
     * 烘的（`render/dying/layer.ts` 的 `#cx/#cy`），而 `FAN_CENTER` 偏了
     * (−20, −14) —— 牌停下来的时候和辉光不同心，看起来就是"牌歪了"。
     */
    const center = { x: ctx.bounds.width / 2, y: ctx.bounds.height / 2 };

    for (const entry of pending) {
      /**
       * **落点。**
       *
       * 两种抽牌走两条不同的路：
       *
       * | | 落点 | 手牌那边 |
       * | --- | --- | --- |
       * | 普通抽牌 | 手牌**留出来的那一格**的完整姿态 | `reserveArrival()` → 落地后 `commitArrival()` |
       * | 抽到致命牌 | `null`（停在正中） | 牌就在手里，只是**藏**起来（`setHidden`） |
       *
       * ⚠️ 手牌给的是**姿态**（坐标 + 旋转 + 尺寸），不是这里估的一个坐标 ——
       * 评审：「手牌代码告诉新牌目的地坐标，旋转角度，尺寸等所有参数」。
       */
      let to: ArrivalPose | null = null;
      if (!entry.hold) {
        const pose = hand.reserveArrival();
        if (pose !== null) {
          to = { x: pose.x, y: pose.y, rotation: pose.roll, scale: pose.scale };
          arriving.push(entry.uid);
          // ★ 视角钉在**抽牌的人**身上（不是当前回合的人）：热座里抽完就换人，
          //   不钉的话这张牌会飞进下一个人的手里
          holdActorId = entry.actor;
        }
      }

      const view_ = new DrawReveal({
        renderer,
        def: entry.def,
        height: REVEAL_CARD_HEIGHT,
        from,
        center,
        to,
        dying: entry.hold,
      });
      if (entry.hold) {
        inFlight.push(entry.uid);
        /**
         * ★ **把濒死压住，等飞到中点再放行。**
         *
         * 评审：「在飞到屏幕中心的动画完成后，所有濒死相关的视觉和 BGM 效果
         * 随即生效。」视图里那个抉择一出现，评分表就已经是"濒死"了 ——
         * 不压的话，横带/辉光/裂缝和 BGM 会在牌还在飞的时候就炸开。
         */
        ctx.energy?.holdDying(true);
        // ★ 渐强从这里开始：抽牌触发濒死那一刻，音量往上爬，打击那一下顶到最大
        ctx.impact?.anticipate();
      }
      effects.addChild(view_.view);
      reveals.push({
        view: view_,
        handedOff: false,
        uid: entry.uid,
        arrival: to !== null,
        dying: entry.hold,
        dyingReleased: false,
        landed: false,
      });
    }
    applyHidden();
  }

  /**
   * **待结算出牌出现时，摆一拨出牌动画**（actor 与 spectator 都放）。
   *
   * 计划 §5：「牌面从出牌处飞到画面中心 → 停 `PLAY_HOLD_MS` → 生效 → 飞入
   * 弃牌堆」；被取消（打断链奇数）则淡出。这里只负责**发现新出牌并创建**，
   * 每帧的推进和"取消与否"在 `updatePlays`。
   *
   * ⚠️ 签名一样就不重摆：打断链增长时 `resolution.play` 一个字没变，
   *    重摆会把已经飞出去的牌拉回来。
   */
  function syncPlayReveals(view_: PlayerView): void {
    const play = view_.resolution?.play ?? null;
    const signature = playSignature(play);
    if (signature === null) {
      lastPlaySignature = null;
      return;
    }
    if (signature === lastPlaySignature) return;
    lastPlaySignature = signature;

    const chrome_ = ensureChrome();
    const origin = chrome_.playOrigin();
    const center = { x: ctx.bounds.width / 2, y: ctx.bounds.height / 2 };
    const count = play === null ? 0 : play.cards.length;
    const captured = pendingPlayFrom;
    let index = 0;
    for (const card of play?.cards ?? []) {
      const def = ctx.registry.get(card.title);
      if (def === undefined) continue;
      /**
       * ★ **从拖拽落点起飞**（actor 自己那一路）；量不到时退回固定锚点
       *   （观众那一路：它看不到 actor 的真实手牌位置）。
       *   两张以上时在起飞处**横向错开一点**，不然它们完全重叠成一张。
       */
      const base = captured?.[index] ?? origin;
      const spread = count > 1 ? (index - (count - 1) / 2) * (REVEAL_CARD_HEIGHT * 0.35) : 0;
      index += 1;
      const reveal = new PlayReveal({
        renderer,
        def,
        height: REVEAL_CARD_HEIGHT,
        faces: hand.faces,
        from: { x: base.x + spread, y: base.y },
        // ★ 中心也按张数横排错开 —— 否则多张飞到中心会完全重叠成一张
        center: { x: center.x + spread, y: center.y },
      });
      effects.addChild(reveal.view);
      plays.push(reveal);
    }
    pendingPlayFrom = null;
  }

  /**
   * 每帧推出牌动画。`cancelled` 由**最新的视图**喂进去（打断链奇偶）——
   * 停顿结束前打断进来都还算数；已经飞向弃牌堆的不再倒回来。
   */
  function updatePlays(ticker: Ticker): void {
    if (plays.length === 0) return;
    const resolution = view().resolution;
    for (let i = plays.length - 1; i >= 0; i--) {
      const reveal = plays[i];
      if (reveal === undefined) continue;
      if (resolution !== null) {
        reveal.setCancelled(resolution.nopeChain.length % 2 === 1);
        reveal.setNopeCount(resolution.nopeChain.length);
        reveal.setWindowOpen(true);
      } else if (view().pending === null) {
        // ⚠️ 别在这一帧再喂 `setCancelled(false)`：它会把最后一帧的奇偶覆盖掉
        reveal.setWindowOpen(false);
      }
      // else：窗口已关，但这一手还欠一个抉择（给牌 / 抽牌 / 捞牌）——
      //       牌得**继续停在中央**等它结算完再消失（用户：打出去就直接消失了）
      reveal.update(ticker);
      if (!reveal.finished) continue;
      reveal.destroy();
      plays.splice(i, 1);
    }
  }

  /**
   * 每帧推抽牌动画，并在它走完之后**交接**。
   *
   * 两条收尾：
   *
   * - **飞进手牌**的那种：告诉手牌"到了"（`commitArrival`），手牌这才把新牌
   *   画进那一格，飞行的这一张随即删掉；同时把钉住的视角放掉（设备这才交给
   *   下一个人）。评审：「新牌到位后告诉手牌代码，令其渲染为添加完牌后的状态
   *   并删除新牌。」
   * - **停在正中**的那种：濒死一结束就开始淡出，淡完解封
   */
  function updateReveals(ticker: Ticker): void {
    if (reveals.length === 0) return;
    let unsealed = false;
    for (let i = reveals.length - 1; i >= 0; i--) {
      const entry = reveals[i];
      if (entry === undefined) continue;

      // ★ 已经交班了：它的去留由 `centeredStage` 说了算（淡出完 → finished）
      if (entry.handedOff) {
        if (!centeredStage.finished) continue;
      } else {
        entry.view.update(ticker);
        /**
         * ★ **落地那一记小 kick**（"牌砸在桌上"）。
         *
         * ⚠️ 它和主打击**必须分开**：这里是扇动的终点、牌刚落到画面正中，
         *    真正的重击在后面 —— 牌先缩一下再撑满（`dyingPop` 的预备动作），
         *    撑满那一帧才是"濒死特效亮相"的时刻（见下一条）。
         */
        if (entry.dying && !entry.landed && entry.view.arrived) {
          entry.landed = true;
          ctx.impact?.land();
        }
        /**
         * ★ **撑满的那一刻 = 主打击**（`docs/dying-impact.md`）。
         *
         * ⚠️ 这里原来是 `arrived`（刚飞到中点）就放开 `holdDying` —— 于是
         *    红世界、故障、BGM 全在牌还只有 **0.82 倍**的时候就亮了，等牌
         *    撑满 2 倍时反而什么都没发生：**打击点被抹平**。现在改成
         *    `holding`（撑满、停在正中），视觉 / 音乐 / 冻结 / 白闪 / 震动
         *    全落在这一帧上。
         */
        if (entry.dying && !entry.dyingReleased && entry.view.holding) {
          entry.dyingReleased = true;
          ctx.energy?.holdDying(false);
          ctx.impact?.hit();
          // ★ 同一帧炸开（两批粒子同时生成，见 `ImpactParticles.burst`）
          particles.burst({ x: ctx.bounds.width / 2, y: ctx.bounds.height / 2 });
        }
        /**
         * ★ **换手：翻牌那一段走完 → 交给"浮在正中的那张牌"。**
         *
         * `holding` 就是 `held` 那一相（停在正中、2 倍大小、正面朝外）。这一刻
         * 两边的姿态**完全一样**，所以换手是看不出来的 —— 而换完之后这一张
         * 就有了手牌那一整套（评审要的"正常手牌的一切属性和功能"）。
         */
        if (entry.dying && entry.view.holding) {
          entry.handedOff = true;
          centeredStage.start(entry.uid);
          entry.view.destroy();
          continue;
        }
        if (!entry.view.finished) continue;
        entry.view.destroy();
      }
      reveals.splice(i, 1);

      if (entry.arrival) {
        const at = arriving.indexOf(entry.uid);
        if (at >= 0) {
          arriving.splice(at, 1);
          // ★ 手牌切成"加完牌"的状态（多出来的那一张就落在刚才预留的格子里）
          hand.commitArrival();
          // 手牌现在才算"加完牌"：强制重喂一次（`syncHand` 平时会跳过同序）
          lastHandOrder = [];
          syncHand();
          // ★ 先别交设备：让人看见那张牌真的在手里了（见 `HANDOFF_MS`）
          if (holdActorId !== null) handoffLeft = HANDOFF_MS;
        }
      } else {
        const at = inFlight.indexOf(entry.uid);
        if (at >= 0) {
          inFlight.splice(at, 1);
          unsealed = true;
        }
      }
    }
    if (unsealed) applyHidden();
  }

  /** 把视角放掉（设备交给下一个人）。这一步**只在没牌在飞的时候**做 */
  function releaseHold(): void {
    if (holdActorId === null) return;
    holdActorId = null;
    game.holdActor(null);
  }

  /**
   * 落袋之后那一下"停顿"（见 `HANDOFF_MS`）。每帧推一次。
   *
   * ⚠️ 用 `deltaMS` 而不是 `setTimeout`：这一层已经有那唯一一个 ticker
   * （`architecture.md` §7.1），再引一条时间轴就会和"离开这一屏"打架 ——
   * 定时器在屏幕销毁之后还会响一次。
   */
  function stepHandoff(deltaMS: number): void {
    if (handoffLeft <= 0) return;
    // 还有牌在飞就**先不倒数**：这一下停顿是给"刚落地的那一张"看的，
    // 后面还有一张在路上的话，等它落地再重新开始数
    if (arriving.length > 0) return;
    handoffLeft -= deltaMS;
    if (handoffLeft > 0) return;
    handoffLeft = 0;
    releaseHold();
    refresh();
  }

  // ============================================================
  // 版面
  // ============================================================

  /**
   * 重建版面。
   *
   * 这一屏**不再自己排一整套面板** —— 桌面装置（回合队列 / 抽牌堆 / 弃牌堆 /
   * 结束回合 / 齿轮）都在 `gameTable.ts` 里，热座和联机共用同一份。
   * 这里只剩两件事：把视图喂给装置、把动作接回来。
   */
  /** "现在该让玩家点哪儿"（纯数据）。见 `tableIntent.ts` */
  function currentIntent(): ReturnType<typeof intentOf> {
    const intent = intentOf(view(), ctx.registry);
    // ★ 五张不同名：现在等玩家点弃牌堆里要捞的那张
    if (fivePick !== null && fivePick.size > 0) {
      return { ...intent, targets: [], insert: [], pick: fivePick };
    }
    const targeting = intent.targets.length > 0 && selection !== null;
    // ⚠️ 只有"拖出去之后还差对象"的时候，队列里的名字才可以点 ——
    //    否则点一下名字什么都不会发生，那比不可点更让人困惑
    return { ...intent, targets: targeting ? intent.targets : [] };
  }

  function build(): void {
    // ① ★ **先摆抽牌动画**：它决定这一帧手牌要不要给某张牌留个位
    //    （`reserveArrival`），也决定要不要把视角钉住。顺序反了的话，
    //    手牌会先按"新牌已经在手里"排一遍，预留的那一格就白留了
    spawnPendingReveals();

    // ② ★ 把视角钉在抽牌的人身上（热座：抽完就换人，牌会飞进下一个人的手里）
    game.holdActor(holdActorId);

    // ★ 报分。热座里"我"就是当前拿着设备的那位 ——
    //    `game.view` 是操作者的视图，所以切座位时音乐会跟着换视角（这是对的）
    reportEnergy(ctx.energy, view(), ctx.registry);

    // ⚠️ 先作废过期的选择，再算 intent —— 顺序反了会把一个已经打不出去的
    //    选择算进 targets 里（队列里亮着人，点下去什么都不会发生）
    validateSelection();
    const chrome_ = ensureChrome();
    const intent = currentIntent();
    chrome_.update(view(), intent);
    // ★ 目标高亮：选定的目标要一直亮到本次行动结束（hover 的由队列自己报）
    chrome_.setTargetHighlight(selection?.target ?? view().resolution?.play.target ?? null);
    // ①' ★ **再摆出牌动画**：视图里出现新的待结算出牌时飞一张
    //    ⚠️ 必须在 `chrome_.update()` **之后**：落点 `discardTop()` 是 layout 算出来的
    syncPlayReveals(view());
    /**
     * ★ **只有"我能出牌"的时候手牌才听 hover / 能拖。**
     *
     * 评审："让牌扇仅在可操作（回合进行中，打出断点，或类似情形）时监听 hover，
     * 其他时候无视 hover"。判据在 `handIsLive()`：**从视图推导**我这手牌现在
     * 有没有用 —— 自由回合出牌、响应窗口里打断、交牌、濒死时用救命牌都算。
     *
     * ⚠️ **不能**拿"轮到我了"当判据：响应窗口里不是我的回合，但我能打断。
     * ⚠️ 濒死也在内：那时候唯一的合法出牌是"把那张 Catch 拖出去"
     *    （`decide-rescue-yes`），漏掉它牌扇就是死的（评审报过）。
     */
    // ★ 判据全部从视图推导（响应窗口里有没有 [断点]、濒死有没有 Catch、是不是
    //   我的自由回合一目了然）—— 不再依赖 `options()`。
    // ★ 已经举起来的那一手必须保持可打：松手要能提交（见 handIsLive 的 `armed`）
    handLive = handIsLive(view(), ctx.registry, armed !== null);
    // ★ 永远可以拖拽换位（迷惑对手）；只有 handLive 时才允许打出去
    hand.setInteractive(!chrome_.trackerOpen());
    hand.setPlayable(handLive);
    syncHand();
    applyHidden();
  }

  /** 点了第 `slot` 张牌背 = 抽这一张。**发一个 index 给房主** */
  function pickBack(slot: number): void {
    const pending = view().pending;
    if (pending === null || pending.kind !== 'stealCard') return;
    game.act({
      t: 'decide',
      actor: view().you,
      decision: { kind: 'stealCard', index: slot },
    });
  }

  /**
   * 有人被抽走了牌 → 翻一张给他看。
   *
   * 评审给的动作：「先生成一个隐藏的实际牌面垫在牌背底下，然后二者一起
   * 左右 pitch 180 度，刚好转到 90 度时牌背隐藏牌面显示，转完删去牌背，
   * 然后牌面进入手牌」。`CardFlip` 就是那一下；牌进手牌由状态更新完成。
   */
  function playStealFlip(def: CardDef): void {
    const flip = new CardFlip({ renderer, def, faces: ctx.faces, height: 132 });
    // 摆在**桌面正中** —— 也就是刚才那一扇牌背的位置，
    // 这样"抽出来的那张"看起来就是从那扇里被拎起来的
    const center = ensureChrome().center();
    flip.view.position.set(center.x - flip.size.width / 2, center.y - flip.size.height / 2);
    effects.addChild(flip.view);
    flips.push(flip);
    flip.flip();
  }

  /** 拖出手牌之后：找到唯一匹配的选项就替他按一下 */
  /**
   * **玩家把手牌拖出去了** —— 找出这一手对应的动作。
   *
   * 判据全在 `tableIntent.ts` 的 `parsePlay()`（纯函数、有测试）：
   * 拖出来的牌 + 视图直接解析成意图；还差"对谁 / 要哪个牌名 / 捞哪张"就继续问。
   * 这一屏只负责把结果接上。
   */
  function playByTitles(played: readonly CardDef[]): void {
    if (played.length === 0) return;
    // ★ 已经举起来了 → 松手就是提交这一手
    if (armed !== null) {
      const action = armed;
      armed = null;
      commit({ t: 'turn/play', actor: action.actor, play: action.play });
      return;
    }
    const used = uidsOfPlayed(played);
    // ★ 拖出来的那张牌在手上找不到 —— 这一手根本不成立（视图刚刚换过）。
    //    手牌必须**回到拖出去之前**，否则那几张牌就凭空消失了
    if (used.length === 0) {
      hand.revert();
      return;
    }

    // 正在等"要哪个牌名"（三张同名第二步）：拖出来的那张就是答案
    if (selection !== null && selection.target !== null) {
      const first = used[0];
      const card = first === undefined ? undefined : view().yourHand.find((c) => c.uid === first);
      if (card !== undefined) {
        const done = parsePlay(view(), selection.cards, { target: selection.target, namedTitle: card.title }, ctx.registry);
        if (done.kind === 'act') {
          selection = null;
          commit(done.action);
          return;
        }
      }
      // 不合法的第二步：**整手作废**，牌回手上（不留一个半截的选择）
      cancelSelection();
      refresh();
      return;
    }

    // ★ 单张的"非出牌"动作（用救命牌 / 交牌 / 打断）：手势直接映射成动作，
    //   不走出牌解析。多张时不会命中这些。
    if (used.length === 1) {
      const only = used[0];
      const decided = only === undefined ? null : handCardAction(view(), only, ctx.registry);
      if (decided !== null) {
        commit(decided);
        return;
      }
    }

    // ★ 出牌：把"拖了哪几张"解析成一条意图（目标 / 牌名 / 捞哪张由视图推导）。
    const result = parsePlay(view(), used, {}, ctx.registry);
    if (result.kind === 'pick') {
      fivePick = result.picks;
      refresh();
      return;
    }
    if (result.kind === 'act') {
      commit(result.action);
      return;
    }
    if (result.kind === 'target') {
      selection = { cards: used, target: null };
      openChooser(used, result.targets);
      refresh();
      return;
    }

    // ★ 解析不出来（不合法的一手）。牌已经从扇形里被摘掉了，必须原地退回
    hand.revert();
  }

  /**
   * **执行一个动作；被拒就把手牌退回去。**
   *
   * 界面只发自己解析得出来的动作，所以"被拒"在正常路径上不该发生
   * —— 但**该发生的时候必须退**：reducer 才是权威，而它是可以拒的
   * （评审：「打出手牌的动作不会验证动作是否合法，也不会适时同步和回退状态」）。
   */
  function commit(action: GameAction): void {
    fivePick = null;
    // ★ 量下这一手牌现在在屏幕上的位置（还在拖拽落点），给出牌动画当起飞点
    if (action.t === 'turn/play') {
      // ⚠️ 单张出牌的 `play` 用的是 `card`（不是 `cards`）—— 以前只读 `cards`，
      //    于是单张出牌永远量不到起飞点、只能回退到固定锚点。
      capturePlayFrom(playCards(action.play));
    } else {
      pendingPlayFrom = null;
    }
    if (!game.act(action)) {
      pendingPlayFrom = null;
      hand.revert();
    }
  }

  /**
   * **把打出的牌现在的牌心位置量下来。**
   *
   * `lastHandOrder[i]` 是喂给手牌的第 i 张牌（uid），`hand.view.boxOf(i)`
   * 是它在画布上的包围盒 —— 拖出去的牌此刻正停在拖拽落点。量不齐就整体
   * 放弃（`null`），让调用方回退到固定锚点，免得半截对半截错。
   */
  function capturePlayFrom(uids: readonly CardUid[]): void {
    if (uids.length === 0) {
      pendingPlayFrom = null;
      return;
    }
    const out: { readonly x: number; readonly y: number }[] = [];
    for (const uid of uids) {
      const at = lastHandOrder.indexOf(uid);
      const box = at >= 0 ? hand.view.boxOf(at) : null;
      if (box === null) continue;
      out.push({ x: box.x + box.width / 2, y: box.y + box.height / 2 });
    }
    pendingPlayFrom = out.length === uids.length ? out : null;
  }

  /**
   * **作废当前那一手。** 三处会用到：点了不该点的地方、按 Esc、
   * 以及视图变了之后那一手已经打不出去（`validateSelection`）。
   */
  function cancelSelection(): void {
    fivePick = null;
    if (selection === null) return;
    selection = null;
    hand.revert();
  }

  /**
   * 视图一换就检查那一手还算不算数。
   *
   * 手里的牌被抢走、回合结束了、响应窗口开了 —— 这些都会让"选好的这一手"
   * 变成一个打不出去的动作。不查的话，队列里会一直亮着人，
   * 而点下去什么都不发生。
   */
  function validateSelection(): void {
    if (selection === null) return;
    const query = selection.target === null ? {} : { target: selection.target };
    const probe = parsePlay(view(), selection.cards, query, ctx.registry);
    if (probe.kind === 'none') cancelSelection();
  }

  /** 把拖出去的那几张牌换成 uid。同一牌名有多张时按手牌顺序取最前面的（等价） */
  function uidsOfPlayed(played: readonly CardDef[]): CardUid[] {
    const pool = view().yourHand.map((card) => card.uid);
    const used: CardUid[] = [];
    for (const def of played) {
      const at = pool.findIndex((uid) => {
        const card = view().yourHand.find((c) => c.uid === uid);
        return card?.title === def.id;
      });
      if (at < 0) continue;
      const [uid] = pool.splice(at, 1);
      if (uid !== undefined) used.push(uid);
    }
    return used;
  }

  /**
   * 正在等"选对象 / 选牌名"的那一手。
   *
   * - `target === null` → 还差对象（队列里可点的人亮着）
   * - `target !== null` 且还有多条候选（差别在 `namedTitle`）→ 还差牌名
   *   （点自己手里的一张表达"要这个牌名"）
   */
  /** 已经"举起来"的那一手（松手前就开打断窗口）；null = 没举 */
  let armed: Extract<GameAction, { t: 'turn/arm' }> | null = null;
  let selection: { cards: readonly CardUid[]; target: PlayerId | null } | null = null;
  /** 五张不同名：5 张已选好，正等玩家点弃牌堆里要捞的那张 */
  let fivePick: ReadonlyMap<CardUid, GameAction> | null = null;

  /**
   * **把候选铺到画面中央**（目标 + 三张同名时还要牌名）。
   *
   * 候选名叫什么由视图来（`players[].name`）；用第一个候选探一下 `parsePlay`，
   * 若是三张同名，它会返回 `name` 和全部候选牌名 —— 于是名字和牌类可以
   * 做成同一块面板，不必再走「先点队列里的人、等第二步弹窗」那条路。
   */
  function openChooser(cards: readonly CardUid[], targets: readonly PlayerId[]): void {
    const v = view();
    const names = targets.map((id) => ({
      id,
      name: v.players.find((player) => player.id === id)?.name ?? id,
    }));
    const first = targets[0];
    const probe =
      first === undefined
        ? ({ kind: 'target' } as const)
        : parsePlay(v, cards, { target: first }, ctx.registry);
    const titles = probe.kind === 'name' ? probe.titles : null;
    ensureChrome().choosePlay({
      targets: names,
      titles,
      onPick: (target, title) => finishChoice(cards, target, title),
    });
  }

  /** 选好目标（和牌名）之后收尾；解析不出来就整手作废 */
  function finishChoice(cards: readonly CardUid[], target: PlayerId, title: CardTitle | null): void {
    const done = parsePlay(view(), cards, { target, namedTitle: title }, ctx.registry);
    if (done.kind === 'act') {
      selection = null;
      commit(done.action);
      return;
    }
    cancelSelection();
    refresh();
  }

  /** 队列里点了一个人 = 给这一手指定对象（居中面板被挡住时的兜底） */
  function pickTarget(id: PlayerId): void {
    if (selection === null) return;
    const cards = selection.cards;
    const done = parsePlay(view(), cards, { target: id }, ctx.registry);
    if (done.kind === 'act') {
      selection = null;
      commit(done.action);
      return;
    }
    selection = { ...selection, target: id };
    if (done.kind === 'name') {
      openChooser(cards, [id]);
      return;
    }
    refresh();
  }

  // ============================================================
  // 同步
  // ============================================================

  const rebuildSoon = (): void => {
    if (dirty) return;
    dirty = true;
    queueMicrotask(() => {
      dirty = false;
      if (destroyed) return;
      rebuildNow();
    });
  };

  function rebuildNow(): void {
    build();
  }

  /** 状态变了一点、要重画（`selection` 变了就走它） */
  function refresh(): void {
    build();
  }

  let destroyed = false;
  /** 上一次 `handIsLive()` 的结论。**只是读数**（冒烟要它证明濒死时牌扇是活的） */
  let handLive = false;
  /** dev 下把它挂到 `dev/registry.ts`；销毁时摘掉 */
  let unpublish: (() => void) | null = null;
  build();

  return {
    container,
    onEnter() {
      // dev：把这一局挂到"挂号处"，`DEV_TOOLS.game` 就能摸到它
      if (import.meta.env.DEV) {
        unpublish = publishGame({
          label: 'hotseat',
          view: () => game.view,
          options: () => game.options,
          act: (action) => game.act(action),
          // ★ 右下角那个按钮的现场：濒死时要变红 + 长按（条子涨到哪儿了）
          buttons: () => ensureChrome().buttons(),
          // ★ 权威状态与桌面现场：DEV_TOOLS 诊断用（见 dev/registry.ts）
          state: () => game.fullState,
          table: () => ({
            actor: game.actor,
            heldActor: game.heldActor,
            locked: game.locked,
            timeout: game.timeout,
            resolution: game.fullState.resolution,
            decision: game.fullState.decision,
            turn: game.fullState.turn,
            armed: armed ?? null,
            handLive,
            handPlayable: hand.playable,
            handHeld: hand.held,
            handOrder: [...hand.order],
            handPlayed: [...hand.played],
            handSelected: [...hand.selected],
            handDragOut: hand.dragOut().map((def) => def.id),
          }),
          // 抽牌动画的现场：冒烟要证明"在飞的那张牌没有同时出现在手牌里"
          effects: () => ({
            // ★ `phaseMs` 是**调参时最想看的那个数**：抽牌动画每一段多长，
            //    由 `drawReveal.ts` 的 `TRAVEL_MS_PER_PX` 和距离决定
            reveals: reveals.map((entry) => ({
              uid: entry.uid,
              // 交班给"浮在正中那张牌"之后 `view` 已经销毁，读数从那一张来
              phase: entry.handedOff ? 'centered' : entry.view.phase,
              phaseMs: entry.handedOff ? 0 : Math.round(entry.view.phaseMs),
              elapsed: entry.handedOff ? 0 : Math.round(entry.view.elapsed),
              handedOff: entry.handedOff,
            })),
            inFlight: [...inFlight],
            /**
             * ★ **正在放的出牌动画**（冒烟要它证明"出牌确实飞了、而且被
             *    打断时会淡出而不是进弃牌堆"）。每个相位多长也一起交出来。
             */
            plays: plays.map((reveal) => ({
              phase: reveal.phase,
              phaseMs: Math.round(reveal.phaseMs),
              elapsed: Math.round(reveal.elapsed),
              cancelled: reveal.cancelled,
            })),
            arriving: [...arriving],
            holdActor: holdActorId,
            handoffLeft: Math.round(handoffLeft),
            pending: pendingReveals.length,
            frames: frameCount,
            /**
             * ★ **对局屏真的拿到了"打击"这个口没有。**
             *
             * 冒烟要它：`ctx.impact` 是可选依赖，漏接的时候**什么都不报**
             * —— 抽到致命牌照样往下走，只是那一下永远不打。
             */
            impactWired: ctx.impact !== undefined,
            handCards: hand.cards.length,
            hidden: hand.hidden,
            /**
             * ★ **浮在正中那张牌**（评审：它要有手牌的一切属性）。
             *
             * 冒烟/控制台要能看见它真的在动：`sway` 是它这一帧的姿态里
             * 空闲摇摆那一项、`alpha` 是淡出进度、`hovered` 是鼠标指着没有。
             */
            /**
             * ★ **粒子现在活着几颗 / 烘了几张贴图**（冒烟要它）：这一把碎屑
             * 从生成到消失都在画面上，只有读数量得出来。
             */
            particles: { alive: particles.count, shapes: particles.shapes },
            /**
             * ★ **牌扇现在听不听 hover / 能不能拖。**
             *
             * 评审报过"濒死期间被错误地设成 false"—— 那时候唯一的合法出牌是
             * `decide-rescue-yes`（把那张 Catch 拖出去），判据漏了它牌扇就是死的。
             * 这个读数让冒烟在**真的濒死局面**里钉住它。
             */
            handLive,
            centered:
              centeredStage.card === null
                ? null
                : {
                    uid: reveals.find((r) => r.handedOff)?.uid ?? null,
                    hovered: centeredStage.card.hovered,
                    alpha: Math.round(centeredStage.card.alpha * 1000) / 1000,
                    pulse: Math.round(centeredStage.card.pulseValue * 1000) / 1000,
                    roll: Math.round(centeredStage.card.card.pose.roll * 10000) / 10000,
                    pitch: Math.round(centeredStage.card.card.pose.pitch * 10000) / 10000,
                    t: Math.round(centeredStage.card.time * 100) / 100,
                    scale: Math.round(centeredStage.card.card.pose.scale * 10000) / 10000,
                    x: Math.round(centeredStage.card.card.pose.x),
                    y: Math.round(centeredStage.card.card.pose.y),
                  },
            // 悬停说明：指着哪一张、摆在哪一边（冒烟用它断言"真的弹出来了"）
            tooltip: tooltipState,
          }),
        });
      }
      // ★ 一局一份账本（见 `audio/energy.ts` 的 tracker）：清掉上一局的
      //    异常计数 / 牌堆基线 / isFinal 闩，然后按当前视图立刻重报一次
      ctx.energy?.reset();
      reportEnergy(ctx.energy, view(), ctx.registry);
      unsubscribe = game.subscribe(rebuildSoon);
      offEvents = game.onEvents((events) => {
        for (const event of events) {
          // ★ 抽到了牌 → 排一段"从牌堆顶翻到正中"的动画（见 `spawnPendingReveals`）
          if (event.t === 'card/drawn') {
            const def = defOfUid(event.card);
            if (def !== undefined) {
              // ⚠️ `event.player` 是**抽牌的人**：热座里 `view().you` 此刻
              //    已经是下一个人了，而这张牌要飞进抽牌人的手里
              pendingReveals.push({ uid: event.card, def, hold: false, actor: event.player });
            }
            continue;
          }
          // ★ 「异常」= 会死人的那张牌。评分表里"每抽到过一次 +0.5"靠这条累加：
          //    它是**事件**，视图里看不出来（牌抽完会被塞回牌堆）。
          //    它同时也是"停在屏幕中央"的那一种（评审："若造成濒死则停留在屏幕中央"）
          if (event.t === 'lethal/drawn') {
            ctx.energy?.noteExceptionDraw();
            const def = defOfUid(event.card);
            if (def !== undefined) {
              pendingReveals.push({ uid: event.card, def, hold: true, actor: event.player });
            }
            continue;
          }
          if (event.t !== 'card/stolen') continue;
          // 事件里只有 uid；牌名要从**抽牌人的手牌**反查 ——
          // 它此刻已经在他手上了，而那一手是本机看得见的（热座）
          // 事件里只有 uid，牌名要从**看得见的地方**反查：那张牌此刻
          // 已经在抽牌人手里了，而那一手在本机是看得见的（热座）
          const source = view();
          const known = [...source.yourHand, ...source.discardPile].find((c) => c.uid === event.card);
          const def = known === undefined ? undefined : ctx.registry.get(known.title);
          if (def !== undefined) playStealFlip(def);
        }
      });
      rebuildSoon();
    },
    onExit() {
      unpublish?.();
      unpublish = null;
      // 离开对局 → 音乐回大厅
      ctx.energy?.reset();
      unsubscribe?.();
      unsubscribe = null;
      offEvents?.();
      offEvents = null;
    },
    update(deltaMS) {
      // 拖拽后的视觉顺序回写成状态
      flushHandOrder();
      // ★ 松手前把有效的一手"举起来"（开打断窗口）
      syncArm();
      void deltaMS;
    },
    /**
     * **音乐敲了一下 → 牌跟着跳一下。**
     *
     * 手牌和牌背都跳（同一屏上两拨牌各跳各的会很显眼）。
     *
     * ⚠️ **不判"这一下是小节还是拍"** —— 宿主管那件事（终局里改跟拍，
     * 见 `ui/beat.ts` 的 `drivesCardPulse()`）。这一屏只管"跳"。
     * 交接给手牌那一句是评审当初留的接口：`Hand.pulse()` 的注释写着
     * "不要让这一层去 import 音频层"。
     */
    onPulse() {
      hand.pulse();
      /**
       * ★ **停在正中那张牌也跟着鼓动**（评审：「随音乐鼓动」）。
       *
       * 它和手牌走的是同一个入口（`CardRig.pulse`）—— 宿主每小节敲一次，
       * 这一层负责转发给"这一屏上所有会动的牌"，牌自己不知道音乐存在。
       */
      centeredStage.pulse();
      chrome?.fan()?.pulse();
    },
    updateFrame(ticker: Ticker) {
      frameCount += 1;
      // ★ **热座的房主定时器**：到点自动关掉打断窗口 / 替掉线的人决定。
      //   联机那边由 Session 的 setTimeout 干这件事；热座挂在唯一这个 ticker 上。
      game.tick(ticker.deltaMS);
      stepHandoff(ticker.deltaMS);
      hand.update(ticker);
      // ⚠️ 必须在 `hand.update()` **之后**：hover 是那一帧算出来的
      /**
       * ★ **浮在正中那张牌每帧推一次**：摆动、hover、随音乐鼓动，以及"濒死结束
       *    就开始淡出"。它和手牌用的是同一个 `CardRig`（见 `centeredCard.ts`）。
       */
      // ★ 塞回异常：牌扇里那张 [异常] 由 `insertFan` 拖（见 gameTable），
      //   正中这张先藏起来，免得两张叠在一起
      const insertingNow = view()?.pending?.kind === 'insertLethal';
      centeredStage.setFollow(insertingNow);
      centeredStage.setVisible(!insertingNow);
      centeredStage.update(ticker, ctx.energy?.isDying === true);
      // ★ 粒子每帧推进（冻结那一帧会跳过整段 —— 画面定格时它也该定格）
      particles.update(ticker.deltaMS);
      ensureTooltip();
      syncTooltip(ticker.deltaMS);
      chrome?.updateFrame(ticker.deltaMS);
      // ★ 记牌器是模态窗口：开着时手牌不吃指针
      hand.setInteractive(!(chrome?.trackerOpen() ?? false));
      hand.setPlayable(handLive);
      // 翻完的牌就删掉 —— 它已经进手牌了（评审：「转完删去牌背，
      // 然后牌面进入手牌」）
      for (let i = flips.length - 1; i >= 0; i--) {
        const flip = flips[i];
        if (flip === undefined) continue;
        if (!flip.finished) continue;
        flip.destroy();
        flips.splice(i, 1);
      }
      updateReveals(ticker);
      // 出牌动画：每一帧按最新视图喂"取消与否"，走完就销毁
      updatePlays(ticker);
    },
    onKey(event) {
      if (event.key !== 'Escape') return false;
      // Esc 先关齿轮菜单（它盖着整屏），关掉了才退屏 —— 不然一个键干两件事
      if (chrome?.menuOpen() === true) {
        chrome.closeMenu();
        return true;
      }
      // 牌名选择器也盖着整屏，先关它
      if (chrome?.chooserOpen() === true) {
        chrome.closeChooser();
        return true;
      }
      // 记牌器也盖着整屏，先关它
      if (chrome?.trackerOpen() === true) {
        chrome.closeTracker();
        return true;
      }
      // 再作废"选了一半的那一手"：牌回手上。这一条同时是那一步的**取消**
      if (selection !== null) {
        cancelSelection();
        refresh();
        return true;
      }
      ctx.stack.pop();
      return true;
    },
    onGamepad(intent) {
      switch (intent) {
        case 'menu':
          ctx.stack.openOverlay(OVERLAY.settings);
          return true;
        case 'endTurn':
          chrome?.endTurn();
          return true;
        case 'tracker':
          chrome?.toggleTracker();
          return true;
        default:
          return false;
      }
    },
    rebuild() {
      rebuildNow();
    },
    destroy() {
      destroyed = true;
      unpublish?.();
      unpublish = null;
      ctx.energy?.reset();
      unsubscribe?.();
      unsubscribe = null;
      for (const flip of flips) flip.destroy();
      flips.length = 0;
      for (const entry of reveals) entry.view.destroy();
      reveals.length = 0;
      for (const reveal of plays) reveal.destroy();
      plays.length = 0;
      lastPlaySignature = null;
      centeredStage.destroy();
      inFlight = [];
      arriving = [];
      pendingReveals = [];
      handoffLeft = 0;
      holdActorId = null;
      game.holdActor(null);
      hand.cancelArrival();
      // ⚠️ 压住的濒死要放掉：留着的话下一次抽到致命牌就没有任何反应了
      ctx.energy?.holdDying(false);
      offEvents?.();
      offEvents = null;
      chrome?.destroy();
      chrome = null;
      tooltip.destroy();
      hand.destroy();
      container.destroy({ children: true });
    },
    // 背景要据此挖洞 —— 画面上的牌就是这些
    sceneCards() {
      return hand.cards;
    },
  };
}

/** 重名的补一个座位号。热座用名字当席位 id，重名开不出局 */
function uniqueNames(names: readonly string[]): string[] {
  const seen = new Map<string, number>();
  return names.map((name, index) => {
    const base = name.trim() === '' ? `PLAYER-${index + 1}` : name;
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return count === 0 ? base : `${base}·${count + 1}`;
  });
}

/**
 * **开这一桌。**
 *
 * 玩家与游戏规则从**大厅**来（`ctx.room`）—— 建了房间就用房间里的那套，
 * 直接从标题页点「本地对局」就用一个默认的三人局。
 *
 * ⚠️ 走的是和真实开局**完全相同的动作序列**（`lobby/join` ×N → `game/start`），
 * 由 `createHotSeat` 保证 —— 热座因此没有一条绕过校验的旁路。
 */
function createTable(ctx: ScreenContext): HotSeat {
  const lobby = ctx.room.get();
  const nickname = displayNickname(ctx.settings.get());

  if (lobby !== null && lobby.players.length >= 2) {
    return createHotSeat({
      // ⚠️ 热座拿**名字**当席位 id，所以两个人同名就开不出局
      //（"开局失败（lobby/join）：你已经在房间里了"）。
      // 默认昵称一模一样，所以这在联机房间里是常态而不是巧合 ——
      // 撞了就按座位补一个后缀
      names: uniqueNames(lobby.players.map((player) => player.nickname)),
      seed: Date.now(),
      config: lobby.config,
    });
  }

  // 没进房间：默认三人局（官方预设），第一个人用当前昵称，另两个是机器人。
  // ⚠️ 把视角钉在真人身上，否则轮到机器人时会把它的手牌亮出来（热座的视图
  //    默认跟着「当前操作者」走）。
  const bots = ['PLAYER-2', 'PLAYER-3'];
  const game = createHotSeat({
    names: [nickname, ...bots],
    seed: Date.now(),
    botNames: bots,
  });
  game.setViewer(playerId(nickname));
  return game;
}
